import { RootState } from '@redux/store';
import {
  dronesAdapter,
  fleetsAdapter,
  flyRoutesAdapter,
  flySectionsAdapter,
  flyZonesAdapter,
  pilotsAdapter,
  strategicDronesAdapter,
} from '@redux/utm/utm.adapters';
import { createSelector } from '@reduxjs/toolkit';
import { selectParameter } from '@utils/common.utils';
import { denormalize } from 'normalizr';

import { Drone, NormalizedDrone, StrategicDrone } from '@/types/utm/drone.types';
import { NormalizedFleet } from '@/types/utm/fleet.type';
import { Pilot } from '@/types/utm/pilot.types';

import { droneSchema } from './utm.schema';

const { selectAll: selectAllPilots, selectEntities: selectPilotEntities } = pilotsAdapter.getSelectors(
  (state: RootState) => state.utm.pilots,
);

const selectPilotByCode = pilotsAdapter.getSelectors((state: RootState) => state.utm.pilots).selectById as (
  state: RootState,
  id: string,
) => Pilot | undefined;

export const PilotSelectors = {
  selectAllPilots,
  selectPilotByCode,
};

const { selectAll: selectAllDrones, selectEntities: selectDroneEntities } = dronesAdapter.getSelectors(
  (state: RootState) => state.utm.drones,
);

const selectDroneByCode = dronesAdapter.getSelectors((state: RootState) => state.utm.drones).selectById as (
  state: RootState,
  id: string,
) => NormalizedDrone | undefined;

export const DroneSelectors = {
  selectAllDrones,
  selectDroneByCode,
};

const { selectAll: selectAllStrategicDrones, selectEntities: selectStrategicDroneEntities } =
  strategicDronesAdapter.getSelectors((state: RootState) => state.utm.strategicDrones);

const selectStrategicDroneById = strategicDronesAdapter.getSelectors((state: RootState) => state.utm.strategicDrones)
  .selectById as (state: RootState, id: number) => StrategicDrone | undefined;

export const StrategicDroneSelectors = {
  selectAllStrategicDrones,
  selectStrategicDroneById,
};

const { selectAll: selectAllFleets, selectEntities: selectFleetEntities } = fleetsAdapter.getSelectors(
  (state: RootState) => state.utm.fleets,
);

const selectFleetByCode = fleetsAdapter.getSelectors((state: RootState) => state.utm.fleets).selectById as (
  state: RootState,
  id: string,
) => NormalizedFleet | undefined;

export const FleetSelectors = {
  selectAllFleets,
  selectFleetByCode,
};

const { selectAll: selectAllFlyZones, selectEntities: selectFlyZoneEntities } = flyZonesAdapter.getSelectors(
  (state: RootState) => state.utm.flyZones,
);

export const FlyZoneSelectors = {
  selectAllFlyZones,
};

const { selectAll: selectAllFlySections, selectEntities: selectFlySectionEntities } = flySectionsAdapter.getSelectors(
  (state: RootState) => state.utm.flySections,
);

export const FlySectionSelectors = {
  selectAllFlySections,
};

const { selectAll: selectAllFlyRoutes, selectEntities: selectFlyRouteEntities } = flyRoutesAdapter.getSelectors(
  (state: RootState) => state.utm.flyRoutes,
);

export const FlyRouteSelectors = {
  selectAllFlyRoutes,
};

const selectEntities = createSelector(
  [
    selectPilotEntities,
    selectStrategicDroneEntities,
    selectDroneEntities,
    selectFleetEntities,
    selectFlyZoneEntities,
    selectFlySectionEntities,
    selectFlyRouteEntities,
  ],
  (pilots, strategicDrones, drones, fleets, flyZones, flySections, flyRoutes) => ({
    pilots,
    strategicDrones,
    drones,
    fleets,
    flyZones,
    flySections,
    flyRoutes,
  }),
);

export const selectDenormalizedDrones = createSelector([selectEntities, selectAllDrones], (entities, drones): Drone[] =>
  drones.map((drone) => denormalize(drone.code, droneSchema, entities)).filter(Boolean),
);

export const selectDenormalizedDrone = createSelector(
  [selectDenormalizedDrones, selectParameter<string>],
  (drones, code): Drone | null => drones.find((drone) => drone.code === code) ?? null,
);
